import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Toolbar,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Popover,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import HardwareTable from './hardware/HardwareTable';
import HardwareForm from './hardware/HardwareForm';
import ConfirmDialog from './hardware/ConfirmDialog';
import hardwareData from '../data/Hardware.json';
import hardwareColumns from '../data/HardwareColumns.json';

type Hardware = {
  id: number;
  oui?: string;
  hclass?: string;
  DisplayName: string;
  manufacturer?: string;
  version?: string;
  protocol?: string;
};

/*const sample: Hardware[] = [
  { id: '1', displayName: '96318REF_60e327_TD-W8968 V4 0x00000001', manufacturer: 'Broadcom', version: 'TD-W8968 V4 0x00000001', protocol: 'tr069' },
  { id: '2', displayName: '60e327_TD-W8968', manufacturer: 'TriplePlay', version: 'TD-W8968 V2', protocol: 'tr069' },
];*/

const CKEY = 'racs-hardware-visible-columns';
const HARDWARE_STORAGE_KEY = 'racs-hardware-data';

const HardwarePage: React.FC = () => {
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [rows, setRows] = React.useState<Hardware[]>([]);

  const persistRows = React.useCallback(async (nextRows: Hardware[]) => {
    setRows(nextRows);

    try {
      const response = await fetch('/api/hardware', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextRows),
      });

      if (!response.ok) {
        throw new Error('Hardware save failed');
      }

      const savedRows = (await response.json()) as Hardware[];
      setRows(savedRows);
      return savedRows;
    } catch (error) {
      console.warn('Failed to persist hardware rows to backend, keeping local state.', error);
      try {
        localStorage.setItem(HARDWARE_STORAGE_KEY, JSON.stringify(nextRows));
      } catch (storageError) {
        console.warn('Failed to save hardware rows to localStorage', storageError);
      }
      return nextRows;
    }
  }, []);

  React.useEffect(() => {
    const loadRows = async () => {
      try {
        const response = await fetch('/api/hardware');
        if (!response.ok) {
          throw new Error('Unable to load hardware data');
        }

        const data = (await response.json()) as Hardware[];
        if (Array.isArray(data) && data.length > 0) {
          setRows(data);
          return;
        }
      } catch (error) {
        console.warn('Falling back to bundled Hardware.json', error);
      }

      setRows(hardwareData as Hardware[]);
      try {
        localStorage.setItem(HARDWARE_STORAGE_KEY, JSON.stringify(hardwareData));
      } catch (storageError) {
        console.warn('Failed to cache fallback hardware rows', storageError);
      }
    };

    loadRows();
  }, []);

/*const columns = [
    { key: 'id', label: 'ID' },
    { key: 'oui', label: 'OUI' },
    { key: 'hclass', label: 'Class' },
    { key: 'DisplayName', label: 'Display Name' },
    { key: 'manufacturer', label: 'Manufacturer' },
    { key: 'version', label: 'Version' },
    { key: 'protocol', label: 'Protocol' },
];*/
const columns = hardwareColumns;


  const [visibleColumns, setVisibleColumns] = React.useState<Record<string, boolean>>(() => {
      const map: Record<string, boolean> = {};
        columns.forEach((c) => {
            map[c.key] = true;
          });
    try {
      const saved = JSON.parse(localStorage.getItem(CKEY) || '{}');
      columns.forEach(c => {
        if (typeof saved[c.key] === 'boolean') map[c.key] = saved[c.key];
      });
    } catch {}

    return map;
});

  // New hardware dialog state
  const [newOpen, setNewOpen] = React.useState(false);
  const [newHw, setNewHw] = React.useState<Hardware>({ id: 0, oui: '', hclass: '', DisplayName: '', manufacturer: '', version: '', protocol: 'telnet' });
  const [editOpen, setEditOpen] = React.useState(false);
  const [editHw, setEditHw] = React.useState<Hardware | null>(null);
  const [deleteId, setDeleteId] = React.useState<number | null>(null);

  // Column popovers
  const [colsAnchor, setColsAnchor] = React.useState<HTMLElement | null>(null);

  const filtered = rows.filter((r) => {
    const q = query.toLowerCase();
    return (
          (r.DisplayName || '').toLowerCase().includes(q) ||
          (r.oui || '').toLowerCase().includes(q) ||
          (r.hclass || '').toLowerCase().includes(q) ||
          (r.manufacturer || '').toLowerCase().includes(q) ||
          (r.version || '').toLowerCase().includes(q) ||
          (r.protocol || '').toLowerCase().includes(q)
    );
  });

  const visible = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const openNew = () => setNewOpen(true);
  const closeNew = () => setNewOpen(false);

  const handleAdd = async () => {
    if (!newHw.DisplayName) return;

    const nextRows = (() => {
      const maxId = rows.length ? Math.max(...rows.map((r) => r.id)) : 0;
      const newRow = { ...newHw, id: maxId + 1 };
      return [newRow, ...rows];
    })();

    await persistRows(nextRows);
    setNewHw({ id: 0, oui: '', hclass: '', DisplayName: '', manufacturer: '', version: '', protocol: 'telnet' });
    closeNew();
  };

  const openEdit = (r: Hardware) => { setEditHw(r); setEditOpen(true); };
  const closeEdit = () => { setEditOpen(false); setEditHw(null); };
  const handleUpdate = async () => {
    if (!editHw) return;
    const nextRows = rows.map((p) => (p.id === editHw.id ? editHw : p));
    await persistRows(nextRows);
    closeEdit();
  };

  const openDelete = (id: number) => setDeleteId(id);
  const closeDelete = () => setDeleteId(null);
  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    const nextRows = rows.filter((r) => r.id !== deleteId);
    await persistRows(nextRows);
    closeDelete();
  };

  const openCols = (e: React.MouseEvent<HTMLElement>) => setColsAnchor(e.currentTarget);
  const closeCols = () => setColsAnchor(null);

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(CKEY, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#e0e0e0' }}>Hardware</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew} sx={{ textTransform: 'none' }}>New Hardware</Button>
      </Box>

      <Paper sx={{ mb: 1, backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ px: 0, gap: 2, justifyContent: 'space-between' }}>
          <Box />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              placeholder="Search Hardware"
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300, backgroundColor: '#151515', borderRadius: 1, input: { color: '#fff' } }}
            />

            <IconButton color="inherit" onClick={() => {}}>
              <FilterListIcon sx={{ color: '#9aa0a6' }} />
            </IconButton>

            <IconButton color="inherit" onClick={openCols}>
              <ViewColumnIcon sx={{ color: '#9aa0a6' }} />
            </IconButton>
          </Box>
        </Toolbar>
      </Paper>

      <Paper sx={{ bgcolor: '#141414', color: '#e0e0e0', borderRadius: 1, p: 1 }}>
        <HardwareTable
          columns={columns}
          visibleColumns={visibleColumns}
          rows={visible}
          page={page}
          rowsPerPage={rowsPerPage}
          count={filtered.length}
          onEdit={openEdit}
          onDelete={openDelete}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={newOpen} onClose={closeNew} fullWidth maxWidth="sm">
        <DialogTitle>New Hardware</DialogTitle>
        <DialogContent>
          <HardwareForm value={newHw} onChange={(v) => setNewHw(v as any)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNew}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Hardware Dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Hardware</DialogTitle>
        <DialogContent>
          <HardwareForm value={editHw ?? ({} as any)} onChange={(v) => setEditHw(v as any)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleteId)} title={"Confirm Delete"} content={"Are you sure you want to delete this hardware?"} onClose={closeDelete} onConfirm={handleConfirmDelete} />

      <Popover open={Boolean(colsAnchor)} anchorEl={colsAnchor} onClose={closeCols} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Box sx={{ p: 2 }}>
          {columns.map((c) => (
            <FormControlLabel key={c.key} control={<Checkbox checked={visibleColumns[c.key]} onChange={() => toggleColumn(c.key)} />} label={c.label} />
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default HardwarePage;
